"use client"

/**
 * How the question bank's Search hub answers — the two are different jobs, not
 * two skins on one box.
 *
 * **Basic** is a form: a keyword, the attributes an item is filed under, and
 * where it came from. It answers "narrow 1,200 rows to the ones tagged Apply,
 * Hard, Cardiology" — a question with an exact answer that Leo would only make
 * fuzzy.
 *
 * **Leo** is a sentence: describe the item and get one ranked list across the
 * whole bank. It answers "something on beta blockade after decompensation",
 * where the author cannot name the folder, the topic tag, or the wording.
 *
 * Set from the profile menu rather than the hub's own header, for the reason
 * theme and home layout are: it is a standing preference about a surface, and a
 * mode switch in the header would spend the hub's most valuable row on a
 * setting most people choose once. Read by the hub, set from two profile menus,
 * so it needs in-document sync — hence the same small external store as
 * `use-home-variant` rather than `usePersistedState`, which only notifies other
 * browser tabs.
 *
 * Shell-global key: this is a preference about a person's habits, the way theme
 * is, not state the product owns. See `exxat-persisted-state.mdc`.
 */

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

export type QuestionSearchMode = "basic" | "leo"

export const QUESTION_SEARCH_MODES: {
  id: QuestionSearchMode
  label: string
  description: string
}[] = [
  {
    id: "basic",
    label: "Basic",
    description:
      "Keyword, attributes, and location. Exact, and it shows its work",
  },
  {
    id: "leo",
    label: "Leo",
    description: "Describe the question. One ranked list across the whole bank",
  },
]

const STORAGE_KEY = "question-search-mode"
const VERSION = 1
const DEFAULT_MODE: QuestionSearchMode = "basic"

const listeners = new Set<() => void>()
let snapshot: QuestionSearchMode | null = null
let hydrated = false

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
  }
}

function isQuestionSearchMode(value: unknown): value is QuestionSearchMode {
  return value === "basic" || value === "leo"
}

function read(): QuestionSearchMode {
  if (hydrated && snapshot) return snapshot
  hydrated = true

  const raw = getStorageItem(STORAGE_KEY)
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        (parsed as { v?: number }).v === VERSION &&
        isQuestionSearchMode((parsed as { d?: unknown }).d)
      ) {
        snapshot = (parsed as { d: QuestionSearchMode }).d
        return snapshot
      }
    } catch {
      // Unparseable payload — fall through to the default.
    }
  }

  snapshot = DEFAULT_MODE
  return snapshot
}

export function setQuestionSearchMode(next: QuestionSearchMode) {
  snapshot = next
  hydrated = true
  setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: next }))
  for (const listener of listeners) listener()
}

export function useQuestionSearchMode(): QuestionSearchMode {
  return React.useSyncExternalStore(subscribe, read, () => DEFAULT_MODE)
}
